import { decryptMessageText, isCiphertextPayload } from '../crypto/messagePayload.ts';

type ReplySnapshot = {
  id?: string | number;
  sender?: string;
  text?: string;
};

type DecryptableMessage = {
  text?: string;
  replyTo?: ReplySnapshot;
  [key: string]: unknown;
};

async function decryptField(
  key: CryptoKey | null,
  ciphertext: string,
  lockedLabel: string
): Promise<string> {
  if (!key) return lockedLabel;
  try {
    return await decryptMessageText(key, ciphertext);
  } catch {
    return '[encrypted — could not decrypt]';
  }
}

async function decryptOneMessage(
  msg: DecryptableMessage,
  key: CryptoKey | null
): Promise<DecryptableMessage> {
  let next: DecryptableMessage = { ...msg };

  if (msg.text && isCiphertextPayload(msg.text)) {
    next.text = await decryptField(key, msg.text, '[encrypted — unlock to read]');
  }

  if (msg.replyTo?.text && isCiphertextPayload(msg.replyTo.text)) {
    const replyText = await decryptField(
      key,
      msg.replyTo.text,
      '[encrypted — unlock to read]'
    );
    next = { ...next, replyTo: { ...msg.replyTo, text: replyText } };
  }

  return next;
}

export async function decryptMessageList<T extends DecryptableMessage>(
  messages: T[] | null | undefined,
  key: CryptoKey | null
): Promise<T[]> {
  if (!messages) return messages as T[];
  const next: T[] = [];
  for (const msg of messages) {
    next.push(await decryptOneMessage(msg, key) as T);
  }
  return next;
}
